import {
  ActionIcon,
  Button,
  Input,
  LoadingOverlay,
  Modal,
  Text,
  Textarea,
  TextInput,
  UnstyledButton
} from "@marketplace/ui";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdAddCircleOutline, MdDeleteSweep } from "react-icons/md";

import { useProductAutomationForm } from "../../hooks/forms/use-product-automation-form";
import { useGetProductAutomationDetailQuery } from "../../hooks/queries/use-get-product-automation-detail-query";

type ProductAutomationModalProps = {
  opened: boolean;
  onClose: () => void;
  productId: string;
  isAutomationActive: boolean;
};

export const ProductAutomationModal: React.FC<ProductAutomationModalProps> = ({
  opened,
  onClose,
  productId,
  isAutomationActive: initialIsAutomationActive
}) => {
  const { t } = useTranslation("product", { keyPrefix: "automationModal" });
  const [isAutomationActive, setIsAutomationActive] = useState(
    initialIsAutomationActive
  );

  // Check for existing automation data when modal opens for reactivation
  const shouldCheckAutomation =
    opened && !initialIsAutomationActive && !!productId;

  const {
    data: automationDetailData,
    isLoading: isCheckingAutomation,
    error: automationError
  } = useGetProductAutomationDetailQuery({
    productId,
    options: {
      enabled: shouldCheckAutomation,
      staleTime: 0,
      retry: false
    }
  });

  // Update isAutomationActive based on fetched data
  useEffect(() => {
    if (!opened) {
      // Reset state when modal closes
      setIsAutomationActive(initialIsAutomationActive);
      return;
    }

    if (shouldCheckAutomation) {
      // If data exists, treat as edit mode
      if (automationDetailData?.data?.product_automation) {
        setIsAutomationActive(true);
      } else if (!isCheckingAutomation) {
        // No existing automation, keep as create mode (only set when loading is done)
        setIsAutomationActive(false);
      }
    } else {
      setIsAutomationActive(initialIsAutomationActive);
    }
  }, [
    opened,
    initialIsAutomationActive,
    shouldCheckAutomation,
    automationDetailData,
    isCheckingAutomation
  ]);

  const { formMethods, onSubmit, isPending } = useProductAutomationForm({
    productId,
    isAutomationActive,
    onSuccess: onClose,
    automationError
  });

  const {
    register,
    control,
    formState: { errors },
    watch,
    reset
  } = formMethods;

  const bodyFields = useFieldArray({
    control,
    name: "body_fields"
  });

  const responseFields = useFieldArray({
    control,
    name: "response_fields"
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!opened) {
      reset();
    }
  }, [opened, reset]);

  const watchedResponseFields = watch("response_fields");

  const generateExpectedResponse = () => {
    const dataObject: Record<string, string> = {};

    watchedResponseFields?.forEach((field) => {
      if (field.key && field.key.trim() !== "") {
        dataObject[field.key] = "";
      }
    });

    return JSON.stringify({ data: dataObject }, null, 2);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text type="heading" weight="semibold">
          {t("title")}
        </Text>
      }
      size={610}
      classNames={{
        header: "!p-6",
        body: "!p-6 !pt-0"
      }}
    >
      <LoadingOverlay visible={isCheckingAutomation} />
      <form onSubmit={onSubmit}>
        <div className="flex flex-col gap-4">
          {/* Endpoint Input */}
          <Textarea
            {...register("link_endpoint")}
            label={t("labels.endpoint")}
            placeholder={t("placeholders.endpoint")}
            error={errors.link_endpoint?.message}
            rows={3}
            disabled={isPending}
            withAsterisk
          />

          {/* Authentication Input */}
          <Textarea
            {...register("authentification")}
            label={t("labels.authentication")}
            placeholder={t("placeholders.authentication")}
            error={errors.authentification?.message}
            rows={3}
            disabled={isPending}
            withAsterisk
          />

          {/* Body Fields */}
          <Input.Wrapper
            label={t("labels.body")}
            error={errors.body_fields?.message}
            withAsterisk
          >
            <div className="flex flex-col gap-y-2">
              {bodyFields.fields.map((field, index) => (
                <div
                  className={clsx("flex flex-col gap-y-2", {
                    "pl-6": bodyFields.fields.length > 1
                  })}
                  key={field.id}
                >
                  <div className="relative">
                    {bodyFields.fields.length > 1 && (
                      <div className="absolute w-6 h-full -left-7 flex items-center">
                        <ActionIcon
                          variant="subtle"
                          onClick={() => bodyFields.remove(index)}
                          disabled={isPending}
                        >
                          <MdDeleteSweep className="h-4 w-4 text-[#FF0606]" />
                        </ActionIcon>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-x-2">
                      <TextInput
                        {...register(`body_fields.${index}.title` as const)}
                        placeholder={t("placeholders.title")}
                        error={
                          (errors.body_fields as any[])?.[index]?.title?.message
                        }
                        disabled={isPending}
                      />
                      <TextInput
                        {...register(`body_fields.${index}.key` as const)}
                        placeholder={t("placeholders.key")}
                        error={
                          (errors.body_fields as any[])?.[index]?.key?.message
                        }
                        disabled={isPending}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex gap-x-2 items-center">
                <hr className="w-full h-px border-0 bg-noble-blue-500" />
                <UnstyledButton
                  className="text-noble-blue-500 flex gap-x-1 items-center"
                  onClick={() =>
                    bodyFields.append({
                      title: "",
                      key: ""
                    })
                  }
                  disabled={isPending}
                >
                  <span>{t("buttons.add")}</span>
                  <MdAddCircleOutline className="h-5 w-5" />
                </UnstyledButton>
                <hr className="w-full h-px border-0 bg-noble-blue-500" />
              </div>
            </div>
          </Input.Wrapper>

          {/* Response Data Fields */}
          <Input.Wrapper
            label={t("labels.responseData")}
            error={errors.response_fields?.message}
            withAsterisk
          >
            <div className="flex flex-col gap-y-2">
              {responseFields.fields.map((field, index) => (
                <div
                  className={clsx("flex flex-col gap-y-2", {
                    "pl-6": responseFields.fields.length > 1
                  })}
                  key={field.id}
                >
                  <div className="relative">
                    {responseFields.fields.length > 1 && (
                      <div className="absolute w-6 h-full -left-7 flex items-center">
                        <ActionIcon
                          variant="subtle"
                          onClick={() => responseFields.remove(index)}
                          disabled={isPending}
                        >
                          <MdDeleteSweep className="h-4 w-4 text-[#FF0606]" />
                        </ActionIcon>
                      </div>
                    )}
                    <TextInput
                      {...register(`response_fields.${index}.key` as const)}
                      placeholder={t("placeholders.responseKey")}
                      error={
                        (errors.response_fields as any[])?.[index]?.key?.message
                      }
                      disabled={isPending}
                    />
                  </div>
                </div>
              ))}

              <div className="flex gap-x-2 items-center">
                <hr className="w-full h-px border-0 bg-noble-blue-500" />
                <UnstyledButton
                  className="text-noble-blue-500 flex gap-x-1 items-center"
                  onClick={() =>
                    responseFields.append({
                      key: ""
                    })
                  }
                  disabled={isPending}
                >
                  <span>{t("buttons.add")}</span>
                  <MdAddCircleOutline className="h-5 w-5" />
                </UnstyledButton>
                <hr className="w-full h-px border-0 bg-noble-blue-500" />
              </div>
            </div>
          </Input.Wrapper>

          {/* Expected Response */}
          <Textarea
            label={t("labels.expectedResponse")}
            value={generateExpectedResponse()}
            rows={5}
            disabled
            description={t("expectedResponseHelp")}
            inputWrapperOrder={["label", "input", "description"]}
          />

          {/* Action Buttons */}
          <div className="flex justify-end gap-x-[14px]">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isPending}
              className="w-[128px]"
            >
              {t("buttons.discard")}
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={isPending}
              className="w-[128px]"
            >
              {t("buttons.create")}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
